import { useState } from 'react';
import { api } from '../services/api';
import './MoveWordDialog.css';

// Move a word to another Word Bank bucket (Chunk W10, 2026-09-26). D1 only:
// POST /api/binder/:id/move, which goes through the W3 move service and writes
// a word_moves history row. Odin's move approves the new bucket. It starts no
// auto-chain and generates nothing. Linking a Related Word to a Game Word is
// done on the Word Bank Links screen, not here.

// Stored bucket values. "Related" is labelled Social only (2026-10-21).
const BUCKET_OPTIONS: { value: string; label: string }[] = [
  { value: 'Game', label: 'Game Words' },
  { value: 'Related', label: 'Social only' },
  { value: 'Bench', label: 'Bench' },
  { value: 'Rejected', label: 'Rejected' },
];

interface MoveWordDialogProps {
  wordId: string;
  wordText: string;
  currentBucket: string;
  onClose: () => void;
  onMoved: () => void;
}

export default function MoveWordDialog({ wordId, wordText, currentBucket, onClose, onMoved }: MoveWordDialogProps) {
  const options = BUCKET_OPTIONS.filter((b) => b.value !== currentBucket);
  const [bucket, setBucket] = useState<string>(options[0]?.value || '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLabel = BUCKET_OPTIONS.find((b) => b.value === currentBucket)?.label || currentBucket;

  const runMove = async () => {
    if (!bucket) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/binder/${encodeURIComponent(wordId)}/move`, { bucket, note: note.trim() || undefined });
      onMoved();
    } catch (err) {
      setError((err as Error).message || 'Move failed. Nothing was changed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="move-dialog-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="move-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="move-dialog-title">Move word</h2>
        <p className="move-dialog-sub">
          Moving <strong>{wordText}</strong> out of {currentLabel}. The move is recorded in its history.
        </p>

        <label className="move-dialog-label">New bucket</label>
        <select className="move-dialog-input" value={bucket} onChange={(e) => setBucket(e.target.value)}>
          {options.map((b) => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>

        <label className="move-dialog-label">Note (optional)</label>
        <input
          type="text"
          className="move-dialog-input"
          placeholder="Why it moved"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && <div className="move-dialog-error">{error}</div>}

        <div className="move-dialog-actions">
          <button className="move-dialog-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="move-dialog-confirm" onClick={() => void runMove()} disabled={submitting || !bucket}>
            {submitting ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
}
